import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Building2, Briefcase, Target, Activity, Scale,
  Kanban, Calculator, MessageSquare, Settings, LogOut, Menu, X, TrendingUp, Layers, Flame, Search, Upload, ShieldCheck, Gavel,
} from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/lib/auth";
import { cx } from "@/lib/format";
import WebSearchWidget from "@/components/WebSearchWidget";

const NAV = [
  { to: "/",            label: "Command Center", icon: LayoutDashboard, key: "dash" },
  { to: "/companies",   label: "Companies",      icon: Building2 },
  { to: "/intel",       label: "Company Intel",  icon: Search },
  { to: "/batch-import", label: "Batch Import",  icon: Upload },
  { to: "/committee-watch", label: "Committee Watch", icon: Gavel },
  { to: "/deals",       label: "Deals",          icon: Briefcase },
  { to: "/origin",      label: "Origin",         icon: Target },
  { to: "/origin/bulk", label: "Bulk Discovery", icon: Layers },
  { to: "/origin/watchlist", label: "Watchlist", icon: Flame },
  { to: "/origin/performance", label: "Origin Perf.", icon: TrendingUp },
  { to: "/risk",        label: "Risk",           icon: Activity },
  { to: "/legal",       label: "Legal",          icon: Scale },
  { to: "/projects",    label: "Projects",       icon: Kanban },
  { to: "/valuations",  label: "Valuations",     icon: Calculator },
  { to: "/ask",         label: "Ask CLARA",      icon: MessageSquare },
];

const MOBILE_TABS = [
  { to: "/",         label: "Home",    icon: LayoutDashboard },
  { to: "/deals",    label: "Deals",   icon: Briefcase },
  { to: "/ask",      label: "Ask",     icon: MessageSquare },
  { to: "/settings", label: "Settings",icon: Settings },
];

function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  return (
    <aside className="h-full w-64 flex flex-col" style={{ background: "#0b0b0e", borderRight: "1px solid rgba(255,255,255,0.06)" }}>
      <div className="px-6 pt-7 pb-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 gradient-gold rounded-md flex items-center justify-center">
            <span className="font-display text-black text-lg font-bold">C</span>
          </div>
          <div>
            <div className="font-display text-xl leading-none tracking-tight">Clara Deals</div>
            <div className="overline mt-1 text-[9px] text-white/40">M&A Intelligence</div>
          </div>
          <div className="ml-auto hidden md:block"><NotificationBell testid="notification-bell" /></div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto no-scrollbar">
        <div className="overline px-3 pb-2 text-white/40">Engines</div>
        {[...NAV, ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: ShieldCheck }] : [])].map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              cx(
                "flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-sm transition-all relative group",
                isActive
                  ? "text-white bg-white/[0.04]"
                  : "text-white/60 hover:text-white hover:bg-white/[0.02]"
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-5" style={{ background: "#C5A059" }} />}
                <item.icon size={17} strokeWidth={1.75} className={isActive ? "text-gold" : ""} />
                <span className="font-medium">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-white/5">
        <NavLink to="/settings" onClick={onNavigate} className="flex items-center gap-3 p-2 rounded-md hover:bg-white/[0.03] group" data-testid="sidebar-profile">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C5A059] to-[#152A55] flex items-center justify-center text-[11px] font-semibold text-black">
            {(user?.name || "U").substring(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-white truncate">{user?.name || "Guest"}</div>
            <div className="text-[10px] text-white/40 truncate">{user?.email || ""}</div>
            <div className="text-[9px] uppercase tracking-[0.16em] text-gold/80 mt-0.5" data-testid="sidebar-role">{user?.role}</div>
          </div>
        </NavLink>
        <button
          onClick={logout}
          data-testid="sidebar-logout"
          className="w-full mt-2 flex items-center gap-2 justify-center py-2 text-[12px] text-white/50 hover:text-white border border-white/5 hover:border-white/20 rounded-md transition-colors"
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </aside>
  );
}

function MobileTopBar({ onMenu, title }) {
  return (
    <div
      className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 backdrop-blur-xl"
      style={{
        paddingTop: "calc(env(safe-area-inset-top) + 10px)",
        paddingBottom: "10px",
        background: "rgba(9,9,11,0.85)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <button onClick={onMenu} data-testid="mobile-menu-btn" className="p-1.5 -ml-1.5 text-white">
        <Menu size={20} />
      </button>
      <div className="font-display text-base tracking-tight">{title || "CLARA"}</div>
      <NotificationBell testid="mobile-notification-bell" align="right" />
    </div>
  );
}

function BottomTabBar() {
  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl"
      style={{
        paddingBottom: "calc(env(safe-area-inset-bottom))",
        background: "rgba(9,9,11,0.85)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div className="grid grid-cols-4">
        {MOBILE_TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.to === "/"}
            data-testid={`mobile-tab-${t.label.toLowerCase()}`}
            className={({ isActive }) =>
              cx(
                "flex flex-col items-center gap-1 pt-2.5 pb-2 text-[10px] transition-colors",
                isActive ? "text-white" : "text-white/50"
              )
            }
          >
            {({ isActive }) => (
              <>
                <t.icon size={19} strokeWidth={1.6} className={isActive ? "text-gold" : ""} />
                <span className="uppercase tracking-[0.14em]" style={{ color: isActive ? "#C5A059" : "inherit" }}>
                  {t.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const activeTitle =
    NAV.find((n) => n.to === location.pathname)?.label ||
    (location.pathname.startsWith("/settings") ? "Settings" : "CLARA");

  return (
    <div className="flex min-h-screen text-white">
      {/* Desktop sidebar */}
      <div className="hidden md:block sticky top-0 h-screen">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="md:hidden fixed inset-0 z-50 bg-black/60"
            />
            <motion.div
              initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }}
              transition={{ type: "tween", duration: 0.25 }}
              className="md:hidden fixed top-0 bottom-0 left-0 z-50 w-72"
            >
              <div className="relative h-full">
                <button onClick={() => setMenuOpen(false)} className="absolute top-4 right-4 z-10 p-1.5 text-white/60" data-testid="mobile-menu-close">
                  <X size={18} />
                </button>
                <Sidebar onNavigate={() => setMenuOpen(false)} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <main className="flex-1 min-w-0 flex flex-col">
        <MobileTopBar onMenu={() => setMenuOpen(true)} title={activeTitle} />
        <div
          className="flex-1"
          style={{
            paddingTop: "calc(env(safe-area-inset-top) + 56px)",
            paddingBottom: "calc(env(safe-area-inset-bottom) + 72px)",
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="pl-safe pr-safe"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
        <BottomTabBar />
      </main>
      <WebSearchWidget />

      {/* Desktop: reset mobile top paddings */}
      <style>{`
        @media (min-width: 768px) {
          main > div[style*="safe-area-inset-top"] {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
